import { useEffect, type FC } from "react";
import styled from "styled-components";

import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import {
  selectCampaignItemsByCampaignId,
  selectCampaignItemsIsLoading,
} from "@ds/modules/content/campaign-items/redux/selectors";
import { campaignItemsActions } from "@ds/modules/content/campaign-items/redux/slice";

import { AggregatedValue } from "@ds/components/entities";
import { IconType } from "@ds/components/icons";
import { InfoAttribute, InfoSection, InfoView, InfoViewCallToAction } from "@ds/components/info-view";
import { getInfoViewTitle } from "@ds/components/info-view/helpers";
import { getProperty } from "@ds/utils/properties";
import { composeRoute } from "@ds/utils/router";
import { CONTENT_PLAYLIST_DETAILS_ROUTE } from "@ds/constants/router/entity-routes";
import { TextLink } from "@ds/components/text-link";
import { Loading } from "@ds/components/loaders/loading";

import { formatMixingStrategy } from "../utils/helpers";
import { selectAggregatedCampaignForSelectedEntities, selectSelectedCampaignIds } from "../redux/selectors";

const PlaylistListing = styled.ul`
  padding-inline-start: 0;
  width: 100%;

  a {
    font-weight: 600;
  }
`;

const PlaylistListingAttributes = styled.div`
  margin-block: 1rem 2rem;
  padding-inline-start: 1rem;
`;

const InterleavedPlaylistAttributes = (item: CampaignItem) => (
  <>
    <InfoAttribute
      name="Randomized"
      value={
        <AggregatedValue
          entity={item}
          field="settings.randomize"
          formatter={(entity, field) => (getProperty(entity, field) ? "Yes" : "No")}
        />
      }
      labelColumns={8}
    />
    <InfoAttribute
      name="Min Gap"
      value={
        <AggregatedValue
          entity={item}
          field="settings.min_gap"
          formatter={(entity, field) => getProperty(entity, field) || 0}
        />
      }
      labelColumns={8}
    />
    <InfoAttribute
      name="Max Consecutive"
      value={
        <AggregatedValue
          entity={item}
          field="settings.max_consecutive"
          formatter={(entity, field) => getProperty(entity, field) || 0}
        />
      }
      labelColumns={8}
    />
  </>
);

const PlaylistAttributes = (item: CampaignItem) => (
  <>
    <InfoAttribute
      name="Weight"
      value={
        <AggregatedValue
          entity={item}
          field="weight"
          formatter={(entity, field) => `${getProperty(entity, field) ?? 0}%`}
        />
      }
      labelColumns={8}
    />
    <InfoAttribute
      name="# Assets"
      value={<AggregatedValue entity={item} field="playlist_items_count" />}
      labelColumns={8}
    />
  </>
);

export const CampaignInfo: FC<{ onCreateCampaign: () => void }> = ({ onCreateCampaign }) => {
  const aggregatedCampaign = useDeepRootSelector(selectAggregatedCampaignForSelectedEntities);
  const selectedCampaignIds = useRootSelector(selectSelectedCampaignIds);
  const campaignItems = useDeepRootSelector(state => selectCampaignItemsByCampaignId(state, selectedCampaignIds ?? []));
  const isLoading = useRootSelector(selectCampaignItemsIsLoading);
  const dispatch = useRootDispatch();

  useEffect(() => {
    for (const campaignId of selectedCampaignIds ?? []) {
      dispatch(campaignItemsActions.fetchCampaignItems({ campaignId }));
    }
  }, [dispatch, selectedCampaignIds]);

  if (aggregatedCampaign) {
    const sortedItems = [...campaignItems].sort((a, b) => a.campaign_order - b.campaign_order);

    return (
      <InfoView
        infoTitle={getInfoViewTitle(aggregatedCampaign)}
        infoDescription={<AggregatedValue entity={aggregatedCampaign} field="description" />}
      >
        <InfoSection title="Campaign Info" icon="pi pi-info-circle">
          <InfoAttribute
            name="Mixing Strategy"
            value={
              <AggregatedValue
                entity={aggregatedCampaign}
                field="mixing_strategy"
                formatter={(entity, field) => formatMixingStrategy(getProperty(entity, field))}
              />
            }
          />
        </InfoSection>
        {selectedCampaignIds?.length === 1 && (
          <InfoSection title={"Playlists (" + aggregatedCampaign.campaign_items_count + ")"} icon="pi pi-info-circle">
            <Loading isLoading={isLoading}>
              <PlaylistListing aria-label={"Playlists - total:" + aggregatedCampaign.campaign_items_count}>
                {sortedItems.map(item => (
                  <li key={item.id} className="flex flex-column w-full">
                    <TextLink to={composeRoute(CONTENT_PLAYLIST_DETAILS_ROUTE, { playlistId: item.playlist_id })}>
                      {item.playlist_name}
                    </TextLink>
                    <PlaylistListingAttributes>
                      {PlaylistAttributes(item)}
                      {aggregatedCampaign.mixing_strategy === "interleaved" && InterleavedPlaylistAttributes(item)}
                    </PlaylistListingAttributes>
                  </li>
                ))}
              </PlaylistListing>
            </Loading>
          </InfoSection>
        )}
      </InfoView>
    );
  }

  return (
    <InfoViewCallToAction
      infoTitle="Select campaign"
      callToActionText="Select a campaign in the table for more info or proceed with creating a new campaign."
      buttonLabel="Create campaign"
      buttonIcon={IconType.Create}
      onActionButtonClick={onCreateCampaign}
    />
  );
};
